import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { Receipt, Calendar, DollarSign, Store, CheckCircle, XCircle, CreditCard, Copy } from "lucide-react";
import { useTranslation } from "@/shared/i18n";

interface TransactionPlan {
  id?: string;
  active?: boolean;
  price?: number;
  store?: 'APPLE' | 'GOOGLE' | 'REVENUE_CAT';
}

interface Transaction {
  id?: string;
  amount?: number;
  currency?: string;
  status?: 'completed' | 'pending' | 'failed' | 'refunded';
  createdAt?: string | number | null;
  store?: 'APPLE' | 'GOOGLE' | 'REVENUE_CAT';
  productId?: string;
  receipt?: string;
  refunded?: boolean;
  plan?: TransactionPlan;
}

interface TransactionsFieldProps {
  transactions?: Transaction[];
}

const getStatusLabels = (t: (key: string) => string) => ({
  completed: t('completed'),
  pending: t('pending'),
  failed: t('failed'),
  refunded: t('refunded'),
});

const statusColors = {
  completed: 'bg-green-500/20 text-green-400 border-green-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  refunded: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

const getStoreLabels = (t: (key: string) => string) => ({
  REVENUE_CAT: t('revenueCat'),
  APPLE: t('appleStore'),
  GOOGLE: t('googlePlay'),
});

const storeColors = {
  REVENUE_CAT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  APPLE: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  GOOGLE: 'bg-green-500/20 text-green-400 border-green-500/30',
};

const formatDate = (dateInput: Transaction['createdAt'], t: (key: string) => string): string => {
  if (dateInput === null || dateInput === undefined || dateInput === '') {
    return t('dateUnavailable');
  }

  const date = typeof dateInput === 'number' ? new Date(dateInput) : new Date(dateInput);

  if (Number.isNaN(date.getTime())) {
    return t('dateUnavailable');
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function formatCurrency(amount?: number, currency: string = 'BRL'): string {
  if (amount === undefined || Number.isNaN(amount)) {
    return '—';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

export function TransactionsField({ transactions }: TransactionsFieldProps) {
  const { t } = useTranslation('profiles');
  const { toast } = useToast();
  const statusLabels = getStatusLabels(t);
  const storeLabels = getStoreLabels(t);

  const handleCopy = async (value: string | undefined, label: string) => {
    if (!value) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        toast({
          title: t('copied'),
          description: `${label} ${t('planIdCopied')}`,
        });
      } else {
        throw new Error(t('clipboardUnavailable'));
      }
    } catch {
      toast({
        title: t('copyError'),
        description: t('copyErrorDescription'),
        variant: "destructive",
      });
    }
  };

  if (!transactions || transactions.length === 0) {
    return (
      <Card className="bg-[#1A1A1A] border-[#333333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center text-base">
            <Receipt className="w-5 h-5 mr-2" />
            {t('transactions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400">{t('noTransactionsFound')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#1A1A1A] border-[#333333]">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between text-base">
          <div className="flex items-center">
            <Receipt className="w-5 h-5 mr-2" />
            {t('transactions')}
          </div>
          <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            {transactions.length} {transactions.length === 1 ? t('transaction') : t('transactions')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[400px] overflow-y-auto pr-4">
          <div className="space-y-4">
            {transactions.map((transaction, index) => (
              <Card key={transaction.id ?? transaction.plan?.id ?? index} className="bg-[#222222] border-[#333333]">
                <CardContent className="p-4 space-y-3">
                  {/* Cabeçalho da Transação */}
                  <div className="flex items-center justify-between pb-2 border-b border-[#333333]">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-semibold text-white">
                        {t('transactionNumberLabel')}{index + 1}
                      </span>
                    </div>
                    {transaction.refunded && (
                      <Badge variant="outline" className={statusColors.refunded}>
                        <XCircle className="w-3 h-3 mr-1" />
                        {t('refunded')}
                      </Badge>
                    )}
                    {!transaction.refunded && transaction.status && (
                      <Badge
                        variant="outline"
                        className={statusColors[transaction.status] || statusColors.completed}
                      >
                        {transaction.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {transaction.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                        {statusLabels[transaction.status] || transaction.status}
                      </Badge>
                    )}
                  </div>

                  {/* Valor */}
                  {transaction.amount !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {t('transactionValue')}
                      </span>
                      <span className="text-sm font-semibold text-white">
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </span>
                    </div>
                  )}

                  {/* Data */}
                  {transaction.createdAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {t('transactionDate')}
                      </span>
                      <span className="text-sm text-white">
                        {formatDate(transaction.createdAt, t)}
                      </span>
                    </div>
                  )}

                  {/* Loja */}
                  {transaction.store && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 flex items-center">
                        <Store className="w-4 h-4 mr-1" />
                        {t('transactionStore')}
                      </span>
                      <Badge
                        variant="outline"
                        className={storeColors[transaction.store]}
                      >
                        {storeLabels[transaction.store]}
                      </Badge>
                    </div>
                  )}

                  {/* Plano associado (quando fornecido pela API) */}
                  {transaction.plan && (
                    <div className="rounded-lg border border-[#333333] bg-[#181818] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-300 flex items-center">
                          <Store className="w-4 h-4 mr-1" />
                          {t('linkedPlan')}
                        </span>
                        <Badge
                          variant={transaction.plan.active ? "default" : "secondary"}
                          className={
                            transaction.plan.active
                              ? "bg-green-500/20 text-green-400 border-green-500/30"
                              : "bg-red-500/20 text-red-400 border-red-500/30"
                          }
                        >
                          {transaction.plan.active ? t('active') : t('inactive')}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{t('transactionId')}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono truncate max-w-[140px]" title={transaction.plan.id}>
                            {transaction.plan.id ?? "—"}
                          </span>
                          {transaction.plan.id && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-gray-300 hover:text-white"
                              onClick={() => handleCopy(transaction.plan?.id, t('planId'))}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{t('planPrice')}</span>
                        <span className="font-semibold text-white">
                          {transaction.plan.price !== undefined ? formatCurrency(transaction.plan.price) : "—"}
                        </span>
                      </div>
                      {transaction.plan.store && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{t('planStore')}</span>
                          <Badge
                            variant="outline"
                            className={storeColors[transaction.plan.store]}
                          >
                            {storeLabels[transaction.plan.store]}
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ID do Produto */}
                  {transaction.productId && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{t('transactionProduct')}</span>
                      <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]" title={transaction.productId}>
                        {transaction.productId}
                      </span>
                    </div>
                  )}

                  {/* ID da Transação */}
                  {transaction.id && (
                    <div className="flex items-center justify-between pt-2 border-t border-[#333333] gap-2">
                      <span className="text-xs text-gray-500">{t('transactionId')}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-mono truncate max-w-[180px]" title={transaction.id}>
                          {transaction.id}
                        </span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-gray-300 hover:text-white"
                          onClick={() => handleCopy(transaction.id, t('transactionId'))}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Receipt (se houver) */}
                  {transaction.receipt && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{t('transactionReceipt')}</span>
                      <span className="text-xs text-gray-400">{t('receiptAvailable')}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
